import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/WarningAmber';
import { Typography } from '@mui/material';
import { memo, ReactNode } from 'react';
import styled from 'styled-components';

import { COLOR, PX_TO_REM } from '@/globalStyles';

export enum ALERT_VARIANT {
    WARNING = 'warning',
    INFO = 'info',
}

interface AlertVariantConfig {
    backgroundColor: string;
    textColor: string;
    icon: ReactNode;
}

const ALERT_VARIANT_MAP: Record<ALERT_VARIANT, AlertVariantConfig> = {
    [ALERT_VARIANT.WARNING]: {
        backgroundColor: COLOR.secondaryLightYellow,
        textColor: COLOR.veryDarkGray,
        icon: <WarningIcon />,
    },
    [ALERT_VARIANT.INFO]: {
        backgroundColor: COLOR.veryLightBlue,
        textColor: COLOR.veryDarkGray,
        icon: <InfoOutlinedIcon />,
    },
};

interface AlertBannerProps {
    className?: string;
    variant: ALERT_VARIANT;
    content: string | ReactNode;
}

const AlertBanner = ({ className, variant, content }: AlertBannerProps) => {
    const config = ALERT_VARIANT_MAP[variant];

    return (
        <div className={className}>
            {config.icon}
            {typeof content === 'string' ? <Typography variant="body2">{content}</Typography> : content}
        </div>
    );
};

const Memoized = memo(AlertBanner);
const StyledAlertBanner = styled(Memoized)<{ variant: ALERT_VARIANT }>`
    display: flex;
    align-items: center;
    gap: ${PX_TO_REM[12]};
    padding: ${PX_TO_REM[12]} ${PX_TO_REM[16]};
    background-color: ${({ variant }) => ALERT_VARIANT_MAP[variant].backgroundColor};
    border-radius: ${PX_TO_REM[8]};
    margin: ${PX_TO_REM[16]} 0;

    svg {
        color: ${({ variant }) => ALERT_VARIANT_MAP[variant].textColor};
        font-size: ${PX_TO_REM[20]};
        flex-shrink: 0;
    }

    p {
        color: ${({ variant }) => ALERT_VARIANT_MAP[variant].textColor};
        font-size: ${PX_TO_REM[12]};
        line-height: ${PX_TO_REM[20]};
        letter-spacing: 0.2px;
        margin: 0;
    }
`;

export default StyledAlertBanner;
export type { AlertBannerProps };
